/**
 * Turning get_ad_efficiency / get_ad_set_efficiency rows into AdEfficiencyRows
 * — and deciding, once, which of them are fixed-price reservations.
 *
 * Split out of insightService for one reason: insightService imports
 * `@/config`, which builds the BROWSER Supabase client at module load. The
 * sync worker runs under tsx in Node and needs exactly the same mapping over
 * exactly the same RPCs, but importing insightService there hangs the process
 * (verified — the import never resolves). The rule for this module is
 * therefore binding, not stylistic: relative imports only, never `@/config`,
 * and nothing that builds a client or touches `window` at load — the sync
 * worker imports this module under tsx, and one alias-style import reaching
 * into the browser boot path hangs it.
 *
 * `mapEfficiencyRows` is the ONLY exported way to obtain a row, and it takes
 * BOTH grains at once, because the fixed-rate verdict needs both: an ad whose
 * own days look like an auction is still a booking when its ad set is one
 * (11 FB R&F ads, ฿2,090, on the audited workspace). A pre-pass each caller
 * had to remember would be the pre-pass runDigests.ts forgot. The single-grain
 * mappers are private for the same reason.
 *
 * A second copy is not a theoretical risk here: the `leadConversions` rule
 * below is a null-preserving special case that a hand-written duplicate would
 * almost certainly get wrong, and getting it wrong turns "this platform cannot
 * report leads" into "this platform reported zero leads".
 *
 * @module services/insights/adEfficiencyRow
 */

import { FIXED_RATE_MAX_SPREAD, FIXED_RATE_MIN_DAYS } from "./insightConstants";
import type { AdEfficiencyRow, FixedRateSource } from "./insightTypes";

/**
 * PostgREST's row ceiling (PGRST_DB_MAX_ROWS / config.toml max_rows).
 *
 * It applies to RPC results too, and Range headers are IGNORED on an RPC POST
 * — both verified against the running API: a 2,500-row function came back
 * `206 Partial Content, Content-Range: 0-999/2500`, and a Range header on
 * get_ad_efficiency changed nothing. So these reads cannot be paged; the only
 * honest option is to notice the ceiling and say so.
 *
 * supabase-js does not treat 206 as an error, so without this check a
 * truncated ranking would present itself as the complete picture.
 */
export const ROW_CAP = 1000;

/** Postgres numeric arrives as a string through PostgREST. */
export const num = (v: unknown): number => {
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
};

/** Null-preserving numeric: null/undefined stay null, everything else is a number. */
const numOrNull = (v: unknown): number | null =>
  v === null || v === undefined ? null : Number(v);

/**
 * Does this unit's OWN row read as a fixed-price reservation?
 *
 * Three-state on the raw key, never through num(): num() turns a missing key
 * into 0, which would make a database that has not replayed migration 085
 * look like a migrated one reporting zero CPM days for every unit.
 *
 * - `cpm_days` present → complete-day p10/p90 spread ≤ FIXED_RATE_MAX_SPREAD
 *   over ≥ FIXED_RATE_MIN_DAYS days. LINE L03 (119.9971 / 120.0000 over 17
 *   days) is the case the old rule missed: two days rounded to ฿119.99.
 * - key absent → the pre-085 rule (one distinct 2dp CPM over ≥ 3 spending
 *   days). Insurance for a stale local database, not a ship path: the
 *   migration lands first.
 */
const ownFixedRate = (r: Record<string, unknown>): boolean => {
  if ("cpm_days" in r) {
    const days = num(r.cpm_days);
    const p10 = numOrNull(r.cpm_p10);
    const p90 = numOrNull(r.cpm_p90);
    if (days < FIXED_RATE_MIN_DAYS || p10 === null || p90 === null || p10 <= 0) return false;
    return (p90 - p10) / p10 <= FIXED_RATE_MAX_SPREAD;
  }
  return num(r.distinct_daily_cpm) === 1 && num(r.days_with_spend) >= FIXED_RATE_MIN_DAYS && num(r.impressions) > 0;
};

/** The fields both grains share, straight off the RPC row. */
const mapBase = (r: Record<string, unknown>): AdEfficiencyRow => ({
  adId: r.ad_id as string,
  adName: (r.ad_name as string) ?? "",
  platform: (r.platform as string) ?? "",
  objective: (r.objective as string | null) ?? null,
  status: (r.status as string) ?? "",
  spend: num(r.spend),
  conversions: num(r.conversions),
  clicks: num(r.clicks),
  impressions: num(r.impressions),
  spendRecent7: num(r.spend_recent7),
  spendPrior7: num(r.spend_prior7),
  convRecent7: num(r.conv_recent7),
  convPrior7: num(r.conv_prior7),
  daysWithSpend: num(r.days_with_spend),
  daysWithSpendCompleted: num(r.days_with_spend_completed),
  distinctDailyCpm: num(r.distinct_daily_cpm),
  spendCompleted: num(r.spend_completed),
  firstSpendDate: (r.first_spend_date as string | null) ?? null,
  lastSpendDate: (r.last_spend_date as string | null) ?? null,
  campaignName: (r.campaign_name as string | null) ?? undefined,
  // Deliberately NOT num(): that coerces null to 0, and null is the whole
  // signal — it says this platform cannot report leads, which is what keeps
  // its contracted CPLs out of the judged list rather than judged against a
  // zero. SUM() in the RPC preserves it the same way.
  leadConversions: numOrNull(r.lead_conversions),
  fixedRate: ownFixedRate(r) ? "self" : null,
  adSetId: (r.ad_set_id as string | null) ?? null,
  // Present only when the RPC returned them, so a pre-085 row does not claim
  // "0 CPM days" or "no format" about columns it never had.
  ...("ad_format" in r ? { adFormat: (r.ad_format as string | null) ?? null } : {}),
  ...("cpm_days" in r
    ? { cpmDays: num(r.cpm_days), cpmP10: numOrNull(r.cpm_p10), cpmP90: numOrNull(r.cpm_p90) }
    : {}),
});

/** Ad grain: the creative-level half (new ads, movers, stopped). */
const mapAdRow = (r: Record<string, unknown>): AdEfficiencyRow => mapBase(r);

/**
 * Ad-set grain of the same shape. The row carries the ranked UNIT's identity in
 * adId/adName — for this call that unit is the ad set, because the ad set is
 * what actually holds a budget. adSetId is then the set's own id.
 */
const mapAdSetRow = (r: Record<string, unknown>): AdEfficiencyRow => ({
  ...mapBase({ ...r, ad_id: r.ad_set_id, ad_name: r.ad_set_name }),
  videoViews: num(r.video_views),
  engagements: num(r.engagements),
});

/** The one predicate every ranking, verdict and benchmark uses to hold a
 *  reservation out. Reads the stamp and nothing else; an untyped mock's
 *  `undefined` reads as not fixed. */
export const isFixedRate = (r: AdEfficiencyRow): boolean =>
  r.fixedRate === "self" || r.fixedRate === "adSet";

/**
 * Both grains, mapped together, with the ad-set verdict inherited onto its ads.
 *
 * `rawSets === null` means the ad-set read failed: ads can then only be
 * "self", and `adSetRows` comes back undefined so the engine falls back to ad
 * grain (`input.adSetRows ?? rows`) instead of treating an empty array as a
 * present-but-empty ranking.
 */
export const mapEfficiencyRows = (
  rawAds: Record<string, unknown>[],
  rawSets: Record<string, unknown>[] | null,
): { rows: AdEfficiencyRow[]; adSetRows: AdEfficiencyRow[] | undefined } => {
  const adSetRows = rawSets === null ? undefined : rawSets.map(mapAdSetRow);
  const fixedSets = new Set((adSetRows ?? []).filter(isFixedRate).map((s) => s.adId));
  const rows = rawAds.map((raw) => {
    const row = mapAdRow(raw);
    const inherited: FixedRateSource =
      row.fixedRate === null && row.adSetId !== null && fixedSets.has(row.adSetId) ? "adSet" : row.fixedRate;
    return inherited === row.fixedRate ? row : { ...row, fixedRate: inherited };
  });
  return { rows, adSetRows };
};
